/* Additive optics only; existing Shopify and homepage scripts own behavior. */
(() => {
  if (window.lscLiquidGlass) {
    window.lscLiquidGlass.init(document);
    return;
  }

  const rootSelector = '.lsc-homepage-c, .lsc-liquid-glass';
  const roots = new Map();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  function enhance(root) {
    if (roots.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const cleanups = [];

    root.querySelectorAll('[data-lsc-tablist]').forEach((list) => {
      const tabs = Array.from(list.querySelectorAll('[data-lsc-tab]'));
      const lens = document.createElement('span');
      lens.className = 'lsc-liquid-tab-lens';
      lens.setAttribute('aria-hidden', 'true');
      lens.hidden = true;
      list.append(lens);
      let frame = 0;
      let disposed = false;

      const measure = () => {
        frame = 0;
        if (disposed || !root.isConnected) return;
        const active = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true'
          && !tab.disabled && tab.getAttribute('aria-disabled') !== 'true');
        if (!active || !list.clientWidth) {
          lens.hidden = true;
          return;
        }
        const tabBounds = active.getBoundingClientRect();
        const listBounds = list.getBoundingClientRect();
        const x = tabBounds.left - listBounds.left + list.scrollLeft - list.clientLeft;
        lens.style.width = `${tabBounds.width}px`;
        lens.style.transform = `translate3d(${x}px, 0, 0)`;
        lens.hidden = false;
        tabs.forEach((tab) => { tab.tabIndex = tab === active ? 0 : -1; });
      };
      const schedule = () => {
        if (!frame && !disposed) frame = window.requestAnimationFrame(measure);
      };
      const mutation = new MutationObserver(schedule);
      tabs.forEach((tab) => mutation.observe(tab, {
        attributes: true, attributeFilter: ['aria-selected', 'aria-disabled', 'disabled'],
      }));
      const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
      if (resize) {
        resize.observe(list);
        tabs.forEach((tab) => resize.observe(tab));
      }
      window.addEventListener('resize', schedule, { passive: true, signal });
      list.addEventListener('scroll', schedule, { passive: true, signal });
      if (document.fonts) document.fonts.ready.then(schedule);
      schedule();
      cleanups.push(() => {
        disposed = true;
        window.cancelAnimationFrame(frame);
        mutation.disconnect();
        if (resize) resize.disconnect();
        lens.remove();
      });
    });

    // One delegated handler per root; writes are coalesced to an animation frame.
    let surface = null;
    let frame = 0;
    let clientX = 0;
    let clientY = 0;
    const reset = () => {
      window.cancelAnimationFrame(frame);
      frame = 0;
      if (surface) {
        surface.style.removeProperty('--lsc-liquid-x');
        surface.style.removeProperty('--lsc-liquid-y');
      }
      surface = null;
    };
    const paint = () => {
      frame = 0;
      if (!surface || !surface.isConnected) return;
      const bounds = surface.getBoundingClientRect();
      surface.style.setProperty('--lsc-liquid-x', `${Math.max(0, Math.min(bounds.width, clientX - bounds.left))}px`);
      surface.style.setProperty('--lsc-liquid-y', `${Math.max(0, Math.min(bounds.height, clientY - bounds.top))}px`);
    };
    root.addEventListener('pointermove', (event) => {
      if (reduceMotion.matches || !finePointer.matches || event.pointerType === 'touch') return;
      if (!(event.target instanceof Element)) return;
      const next = event.target.closest('[data-lsc-liquid-edge]');
      if (!next || !root.contains(next)) { reset(); return; }
      if (next !== surface) { reset(); surface = next; }
      clientX = event.clientX;
      clientY = event.clientY;
      if (!frame) frame = window.requestAnimationFrame(paint);
    }, { passive: true, signal });
    root.addEventListener('pointerout', (event) => {
      if (surface && !surface.contains(event.relatedTarget)) reset();
    }, { passive: true, signal });
    root.addEventListener('pointercancel', reset, { passive: true, signal });
    reduceMotion.addEventListener('change', reset, { signal });
    finePointer.addEventListener('change', reset, { signal });
    cleanups.push(reset);

    roots.set(root, () => {
      controller.abort();
      cleanups.forEach((cleanup) => cleanup());
      roots.delete(root);
    });
  }

  const init = (scope) => {
    roots.forEach((cleanup, root) => {
      if (!root.isConnected) cleanup();
    });
    if (scope.matches && scope.matches(rootSelector)) enhance(scope);
    scope.querySelectorAll(rootSelector).forEach(enhance);
  };
  window.lscLiquidGlass = { init };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  // Native product swaps attach a new tree; wait for its children before init.
  // No commerce subscription, broad subtree observer, or idle animation loop.
  document.addEventListener('product-info:loaded', (event) => {
    const scope = event.target;
    window.requestAnimationFrame(() => {
      if (scope.isConnected) init(scope);
    });
  });
  document.addEventListener('shopify:section:unload', (event) => {
    roots.forEach((cleanup, root) => {
      if (event.target === root || event.target.contains(root)) cleanup();
    });
  });
})();
